import { prefetchErpNavigation } from './prefetchErpNavigation';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Lock, Menu, X } from 'lucide-react';
import { ErpUnlockScreen } from './ErpUnlockScreen';
import { ErpAdminSidebar, navItemsForRole } from './ErpAdminSidebar';
import { cn } from '../../utils/cn';
import {
    canAccessErpPath,
    clearErpAccess,
    ErpRole,
    erpLandingPath,
    getErpRole,
    isPublicErpPath,
} from './erpAccess';

interface ErpLayoutProps {
    children: React.ReactNode;
}

export const ErpLayout: React.FC<ErpLayoutProps> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isPublicErpPage = isPublicErpPath(location.pathname);
    const [role, setRole] = useState<ErpRole | null>(() => getErpRole());
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isUnlocked = role !== null;
    const navItems = useMemo(() => (role ? navItemsForRole(role) : []), [role]);

    const handleUnlock = (nextRole: ErpRole) => {
        // The unlock screen already stored the backend-verified session.
        setRole(nextRole);
        navigate(erpLandingPath(nextRole), { replace: true });
    };

    const handleLock = () => {
        clearErpAccess();
        setRole(null);
    };

    // Bounce back to the login screen if the token expires mid-session.
    useEffect(() => {
        const onExpired = () => setRole(null);
        window.addEventListener('supplierAuthExpired', onExpired);
        return () => window.removeEventListener('supplierAuthExpired', onExpired);
    }, []);

    // Keep the current role out of pages it may not open (e.g. a supplier typing an
    // admin URL) — redirect to that role's landing tab.
    useEffect(() => {
        if (!isUnlocked || isPublicErpPage || !role) return;
        if (!canAccessErpPath(role, location.pathname)) {
            navigate(erpLandingPath(role), { replace: true });
            setMobileMenuOpen(false);
        }
    }, [isUnlocked, isPublicErpPage, role, location.pathname, navigate]);

    if (!isUnlocked && !isPublicErpPage) {
        return <ErpUnlockScreen onUnlock={handleUnlock} />;
    }

    if (isPublicErpPage) {
        return (
            <div className="erp-layout-shell min-h-screen bg-bgb">
                <main className="erp-layout-main min-h-screen bg-bgb">
                    {children}
                </main>
            </div>
        );
    }

    const activeNav = navItems.find(item => item.isActive(location.pathname));
    const canGoBack = location.pathname !== '/erp' && !navItems.some(item => item.path === location.pathname);
    const handleMobileNav = (path: string | null) => {
        if (!path) return;
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <div className="erp-layout-shell flex h-screen overflow-hidden bg-bgb">
            <div className="hidden md:block h-full shrink-0">
                <ErpAdminSidebar onLock={handleLock} role={role!} />
            </div>

            <div className="md:hidden fixed inset-x-0 top-0 z-40 bg-w/95 backdrop-blur border-b border-b02">
                <div
                    className="h-[60px] px-3 flex items-center gap-2"
                    style={{ paddingTop: 'env(safe-area-inset-top)' }}
                >
                    <button
                        type="button"
                        onClick={() => canGoBack ? navigate(-1) : setMobileMenuOpen(true)}
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-b4 hover:bg-b02 transition-colors"
                        aria-label={canGoBack ? '返回' : '打开菜单'}
                    >
                        {canGoBack ? <ChevronLeft className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    <button
                        type="button"
                        onClick={() => setMobileMenuOpen(true)}
                        className="min-w-0 flex-1 text-left"
                    >
                        <div className="text-[11px] text-b4 leading-tight">ERP</div>
                        <div className="text-sm font-semibold text-b truncate">{activeNav?.label ?? '管理后台'}</div>
                    </button>
                    <button
                        type="button"
                        onClick={handleLock}
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-b4 hover:bg-b02 transition-colors"
                        aria-label="退出管理"
                    >
                        <Lock className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {mobileMenuOpen && (
                <div className="md:hidden fixed inset-0 z-50 bg-b/30" onClick={() => setMobileMenuOpen(false)}>
                    <div
                        className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-w shadow-2xl border-t border-b02 px-4 pt-4 pb-6"
                        style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-base font-semibold text-b">ERP 导航</div>
                                <div className="text-xs text-b4 mt-0.5">选择要操作的模块</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(false)}
                                className="w-9 h-9 rounded-full flex items-center justify-center text-b4 hover:bg-b02"
                                aria-label="关闭菜单"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {navItems.map(({ label, Icon, path, isActive }) => {
                                const active = isActive(location.pathname);
                                return (
                                    <button
                                        key={label}
                                        type="button"
                                        onClick={() => handleMobileNav(path)}
                                        onPointerEnter={() => prefetchErpNavigation(path)}
                                        onFocus={() => prefetchErpNavigation(path)}
                                        onPointerDown={() => prefetchErpNavigation(path)}
                                        className={cn(
                                            'h-14 rounded-2xl border flex items-center gap-3 px-3 text-left transition-colors',
                                            active
                                                ? 'border-btn-primary/20 bg-btn-primary/10 text-btn-primary'
                                                : 'border-b02 bg-b01 text-b hover:bg-b02'
                                        )}
                                    >
                                        <Icon className="w-4 h-4 shrink-0" />
                                        <span className="text-sm font-medium">{label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <main className="erp-layout-main flex-1 overflow-y-auto bg-bgb pt-[60px] md:pt-0">
                {children}
            </main>
        </div>
    );
};
